import { useState, useEffect, useRef } from 'react';

interface UseShadowOptions {
  /** Shadow DOM挂载的根元素（默认挂到body） */
  rootEl?: HTMLElement;
  /** 要注入到Shadow DOM中的样式文本 */
  style?: string;
  /** Shadow内部容器的类名 */
  containerClasses?: string[];
  /** 包裹Shadow的外层元素类名 */
  wrapperClasses?: string[];
  /** 包裹元素的内联样式 */
  wrapperStyle?: React.CSSProperties;

  extraHtml?: string;

  id: string;
}

/**
 * 通用的创建Shadow DOM的Hook
 * @param options 配置项：样式、根元素、类名等
 * @returns Shadow DOM内部的容器元素（用于React Portal挂载）
 */
export const useShadow = ({
  id,
  style = '',
  rootEl,
  containerClasses = [],
  wrapperClasses = [],
  wrapperStyle = {},
  extraHtml = '',
}: UseShadowOptions): { shadowContainer: HTMLElement | null; destroy: () => void } => {
  const [shadowContainer, setShadowContainer] = useState<HTMLElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    wrapperRef.current?.classList.add(...wrapperClasses);
    shadowContainer?.classList.add(...containerClasses);
  }, [wrapperClasses, containerClasses]);

  useEffect(() => {
    if (wrapperRef.current) {
      Object.entries(wrapperStyle).forEach(([key, value]) => {
        if (value !== undefined) {
          // @ts-ignore
          wrapperRef.current!.style[key as any] = value;
        }
      });
    }
  }, [wrapperStyle]);

  useEffect(() => {
    // Cleanup previous shadow container if it exists
    if (wrapperRef.current && wrapperRef.current.parentNode) {
      wrapperRef.current.parentNode.removeChild(wrapperRef.current);
      wrapperRef.current = null;
    }

    try {
      // 1. 创建外层包裹元素（挂载Shadow Root的载体）
      const wrapperEl = document.createElement('div');
      wrapperEl.classList.add(...wrapperClasses, 'wrapper');
      // wrapperEl.style.height = '100%';
      wrapperRef.current = wrapperEl;

      // apply wrapper styles
      Object.entries(wrapperStyle).forEach(([key, value]) => {
        if (value !== undefined) {
          // @ts-ignore
          wrapperEl.style[key as any] = value;
        }
      });

      // 2. 创建并挂载Shadow Root
      const shadowRoot = wrapperEl.attachShadow({ mode: 'open' });

      // 注入额外的HTML内容
      if (extraHtml) {
        shadowRoot.innerHTML = extraHtml;
      }

      // 3. 注入样式到Shadow DOM
      if (style) {
        const styleEl = document.createElement('style');
        styleEl.textContent = style;
        shadowRoot.appendChild(styleEl);
      }

      // 4. 创建Shadow内部的容器（最终挂载React Portal的元素）
      const containerEl = document.createElement('div');
      containerEl.classList.add(...containerClasses, 'shadow-container');
      shadowRoot.appendChild(containerEl);

      // 5. 将外层元素挂载到指定根节点
      const mountRoot = rootEl || document.body;
      mountRoot.appendChild(wrapperEl);

      setShadowContainer(containerEl);
    } catch (error) {
      console.error('创建Shadow DOM失败:', error);
      setShadowContainer(null);
    }

    // Cleanup on unmount or when rootEl changes
    // return () => {
    //   if (wrapperRef.current && wrapperRef.current.parentNode) {
    //     wrapperRef.current.parentNode.removeChild(wrapperRef.current);
    //     wrapperRef.current = null;
    //   }
    // };
  }, [rootEl]); // Recreate when rootEl changes

  if (shadowContainer) {
    shadowContainer.id = id;
  }

  return {
    shadowContainer,
    destroy: () => {
      if (wrapperRef.current && wrapperRef.current.parentNode) {
        wrapperRef.current.parentNode.removeChild(wrapperRef.current);
        wrapperRef.current = null;
      }
    },
  };
};
